import React, { FunctionComponent, useMemo } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import * as H from 'history';
import styled from 'styled-components';

import {
  ContextTypes,
  FooterTeaser,
  GetTradingBlockSiteName,
  Header,
  Section,
  SiteLogo,
  SiteName,
  SubHeader,
  Svg,
  TradingBlockLink,
} from '@tradingblock/components';
import { config } from '../clientConfig';
import { TwoFactorAuthenticationSetup } from '@tradingblock/components';
import { ApiFactory, GetUrlParams } from '@tradingblock/api';
import { getConfig, getEnvironment, getStaticPath, toHashParams } from '../utils';
import { LoginCardAuthFooter } from './LoginCardAuthFooter';
import { SiteGrp } from '@tradingblock/types';

const StyledTwoFactorAuthenticationSetupWrapper = styled.div`
  .login-container {
    align-items: flex-start;
  }

  .radiobtn {
    border: 1px solid var(--teal);
  }

  .checkcontainer input:checked + .radiobtn {
    border-color: var(--teal);
    background: white;
  }

  .checkcontainer input:checked + .radiobtn:after {
    background: var(--teal);
  }
`;

const TwoFactorAuthenticationSetupWrapper: FunctionComponent<RouteComponentProps> = ({ location }) => {
  const siteGrp = config.siteGrp as SiteGrp;
  const env = getEnvironment(location);
  const isVirtual = env === 'virtual';
  const environmentConfig = getConfig(env);
  const siteParam = siteGrp ? `?grp=${siteGrp}` : '';
  const accountGroup: 'mb' | 'tb' = siteGrp === 'mb' ? 'mb' : 'tb';
  const siteName = GetTradingBlockSiteName(undefined, siteGrp);
  const urlParams = GetUrlParams(location.search);
  const token = urlParams['token'] as string;
  const accessToken = urlParams['accessToken'] as string;
  const redirectRoute = decodeURIComponent(urlParams['redirectRoute'] || '');
  const accountId = Number.parseInt(urlParams['accountId'] as string);

  document.title = `${siteName ? `${siteName} - ` : ''}Two-factor Authentication Setup`;

  const api = useMemo(() => ApiFactory(config.dashboard.tradingApi, accessToken), [accessToken]);

  const history: H.History<{}> = useHistory();

  const handleSetupComplete = () => {
    if (redirectRoute) {
      history.push(redirectRoute);
    } else {
      // For external redirects to dashboard service, the base URL already includes
      // the branch prefix (from env vars modified by pipeline). The 'next' parameter should be
      // a path relative to that service's basename, not include the branch prefix again.
      // The external service (dashboard) has its own BrowserRouter with basename,
      // so we should pass the path without the branch prefix in the 'next' parameter.
      window.location.href = `${config.dashboard.defaultRedirect}/auth?next=/${toHashParams({ id_token: token, grp: siteGrp })}`
    }
  };

  const generateHandleAccountUpdateUrl = (accountUpdateType: 'email' | 'phone') => {
    const urlSearchParams = new URLSearchParams();

    urlSearchParams.append('showAccountUpdateModal', accountUpdateType);
    urlSearchParams.append('accountHolderIndex', '0');

    // For external redirects to dashboard service, the base URL already includes
    // the branch prefix (from env vars modified by pipeline). The 'next' parameter should be
    // a path relative to that service's basename, not include the branch prefix again.
    // The external service (dashboard) has its own BrowserRouter with basename,
    // so we should pass the path without the branch prefix in the 'next' parameter.
    return `${config.dashboard.defaultRedirect}/auth?next=${encodeURIComponent(`/?${urlSearchParams.toString()}`)}${toHashParams({ id_token: token, grp: siteGrp })}`
  }
  
  return (
    <StyledTwoFactorAuthenticationSetupWrapper>
      <Header>
        <SubHeader>
          <SiteLogo siteGroup={siteGrp} />

          <nav className="nav-utilities">
            <ul>
              <li>
                <TradingBlockLink
                  siteGroup={siteGrp}
                  origin={siteGrp}
                  to="LegacySiteHomeUrl"
                >
                  &uarr; Go to <SiteName siteGroup={siteGrp} /> Legacy
                </TradingBlockLink>
              </li>
              <li>
                <TradingBlockLink
                  siteGroup={siteGrp}
                  origin={siteGrp}
                  to="CustomerServiceUrl"
                >
                  Contact us
                </TradingBlockLink>
              </li>
            </ul>
          </nav>
        </SubHeader>
      </Header>
      <main className="login">
        <Section>
          <div className="content">
            <div className="login-container">
              <div className="login-primary">
                <noscript>
                  <div className="alert alert-danger">
                    <h4 className="error">Javascript is required for this site.</h4>
                  </div>
                </noscript>

                <TwoFactorAuthenticationSetup
                  handleEmailUpdate={() => window.location.href = generateHandleAccountUpdateUrl('email')}
                  handlePhoneUpdate={() => window.location.href = generateHandleAccountUpdateUrl('phone')}
                  primaryBtnClassName="btn btn-block btn-light btn-icon"
                  primaryBtnIcon={<Svg path="icon-arrow" />}
                  primaryBtnIconClassName="tb-icon"
                  accountId={accountId}
                  mainHeaderHtmlTag="h1"
                  api={api}
                  handleSetupComplete={handleSetupComplete}
                  mainHeaderStyles={{ fontSize: '2rem' }}
                  mainHeaderIconStyles={{ fontSize: '2rem' }}
                  rootRestHubUrl={config.dashboard.tradingApi}
                  handleAuthTokenExpiration={() => history.push('/')}
                  context={ContextTypes.login}
                  siteGrp={siteGrp}
                />
                <LoginCardAuthFooter
                  isVirtual={isVirtual}
                  group={accountGroup}
                  siteParam={siteParam}
                  environmentConfig={environmentConfig}
                  recovery={
                    <TradingBlockLink
                      className="login-primary-recovery-link"
                      siteGroup={siteGrp}
                      origin={siteGrp}
                      to="CustomerServiceUrl"
                    >
                      Contact us
                    </TradingBlockLink>
                  }
                />
              </div>
              <div className="login-secondary">
                <img alt="" aria-hidden="true" src={getStaticPath('/images/sign-in-hero.png')} />
              </div>
            </div>
          </div>
        </Section>
      </main>
      <footer className="footer">
        <div className="content">
          <FooterTeaser siteGroup={siteGrp} />
        </div>
      </footer>
    </StyledTwoFactorAuthenticationSetupWrapper>
  );
}

export default TwoFactorAuthenticationSetupWrapper;
